<template>
  <q-layout
    class="dashboard-layout"
    :class="{ 'dashboard-layout__staging': isStagingEnvironment }"
  >
    <q-page-container>
      <q-page class="tw-flex tw-flex-col tw-overflow-hidden">
        <status-bar />

        <div class="tw-flex tw-flex-1 tw-flex-row tw-overflow-hidden">
          <Menu />
          <mobile-menu />

          <div
            class="tw-flex tw-h-full tw-flex-1 tw-flex-col tw-overflow-hidden"
          >
            <Header />

            <div id="scrollable-content" class="content">
              <div
                v-if="!hideTitle && route.meta.hideTitle !== true"
                class="tw-mx-auto tw-flex tw-w-full tw-items-center tw-gap-2 tw-py-4"
                :class="{ 'tw-max-w-[1400px]': isFullWidth === false }"
              >
                <h2>{{ route.meta.title }}</h2>

                <div
                  v-if="pageTitleActions.length"
                  class="tw-ml-auto tw-flex tw-flex-wrap tw-gap-2"
                >
                  <c-button
                    v-for="action in pageTitleActions"
                    :key="action.label"
                    :label="action.label"
                    :outline="action.outline"
                    :icon="action.icon"
                    :data-test="action.dataTest"
                    @click="action.onClick"
                  />
                </div>
              </div>

              <div
                class="tw-mx-auto tw-w-full"
                :class="{
                  'tw-max-w-[1400px]': isFullWidth === false,
                  'tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white':
                    !hideBackgroundPanel,
                }"
              >
                <div
                  v-if="!hideSubmenu && subMenuTabs.length > 0"
                  class="tw-relative tw-z-50 tw-w-full"
                >
                  <navigation-tabs mode="route" :tabs="subMenuTabs" />
                </div>

                <div
                  id="main-content"
                  class="main-layout__content"
                  :class="{ 'tw-p-4': !hideBackgroundPanel }"
                >
                  <slot />
                </div>
              </div>

              <countdown-authenticator
                v-if="showCountdownModal"
                :timer="timer"
                @reset-timer="resetTimer"
                @logout="logout"
              />

              <celebration-component
                v-if="celebrationVisible"
                :celebration-type="celebrationType"
              />

              <back-to-top />

              <nps-modal v-if="showNps" @close-modal="showNps = false" />

              <Footer />
            </div>
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script setup>
import BackToTop from "components/global/BackToTop.vue";
import CelebrationComponent from "components/global/celebration/CelebrationComponent.vue";
import NavigationTabs from "shared/components/NavigationTabs.vue";
import { useSubMenuTabs } from "shared/composables/useSubMenuTabs";
import Header from "components/layout/Header.vue";
import Menu from "components/layout/Menu.vue";
import MobileMenu from "components/layout/MobileMenu.vue";
import StatusBar from "components/layout/StatusBar.vue";
import { pageTitleActions } from "shared/composables/usePageTitleActions";
import CountdownAuthenticator from "components/modals/auth/CountdownAuthenticator.vue";
import NpsModal from "components/modals/nps/NpsModal.vue";
import { storeToRefs } from "pinia";
import { useCelebrationStore } from "store/modules/celebrationStore.js";
import { useMenuStore } from "store/modules/menuStore.js";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import Footer from "components/layout/Footer.vue";

const props = defineProps({
  hideBackground: {
    type: Boolean,
    default: () => false,
  },
  hideWrapper: {
    type: Boolean,
    default: false,
  },
  hideTitle: {
    type: Boolean,
    default: false,
  },
});

const celebrationStore = useCelebrationStore();
const { celebrationVisible, celebrationType } = storeToRefs(celebrationStore);

const menuStore = useMenuStore();
const { hideSubmenu } = storeToRefs(menuStore);
const { subMenuTabs } = useSubMenuTabs();
const showNps = ref(false);

const route = useRoute();

/**
 * Either the prop or the route may switch the white panel off — the same prop-or-meta pairing
 * this layout already uses for `hideTitle`. A page that renders its own containers (the
 * student's gesprekdetail) sets `meta.hideBackground`, so it is not a white card inside a
 * white card.
 */
const hideBackgroundPanel = computed(
  () => props.hideBackground === true || route.meta.hideBackground === true,
);

const isFullWidth = computed(() => route.meta.fullWidth === true);

onMounted(() => {
  showNps.value = localStorage.getItem("show_nps") !== "false";
});
</script>

<script>
// TODO: Refactor this to composition API
import { clearAuthStorage, isStagingEnvironment } from "composables/useHelpers";
import { forgetIntendedPath } from "core/auth/intendedPath";

export default {
  setup() {
    const route = useRoute().name;
    return { route };
  },
  data() {
    return {
      minutes: null,
      seconds: null,
      mountedInterval: null,
      showCountdownModal: false,
      countdownText: "",
      isRefreshing: false,
      noInteractionTimer: 0,
      timer: null,
    };
  },
  mounted() {
    // TODO: Put this in a store so it will always works regardless if this component is mounted
    let self = this;

    //interval for no activity
    //Adds +1 every one second, reset on user interaction
    setInterval(function () {
      self.noInteractionTimer = self.noInteractionTimer + 1000; //add one second

      //No activity for 30 minutes
      let maxMinutes = 1800000; //30minutes
      let fiveMinutes = 300000;

      //test
      //maxMinutes = 90000; //1 minutes test 10 second test (1 minute = 60000)

      if (self.noInteractionTimer > maxMinutes - fiveMinutes) {
        let millisLeft = maxMinutes - self.noInteractionTimer; //current - (max + 5 minutes)

        //calculate time left
        let timeLeft = self.millisToMinutesAndSeconds(millisLeft);
        self.timer = timeLeft;
        let minutes = timeLeft[0];
        let seconds = timeLeft[1];

        //Show countdown bar
        self.showCountdownModal = true;

        //Logout if time is expired
        if (minutes < 1 && seconds < 1) {
          self.logout();
        }
      }
    }, 1000);

    //this.countDown(this.buildEndTime());

    document.body.addEventListener("mousemove", this.handleInteraction);
    document.body.addEventListener("scroll", this.handleInteraction);
    document.body.addEventListener("keydown", this.handleInteraction);
    document.body.addEventListener("click", this.handleInteraction);
    document.body.addEventListener("touchstart", this.handleInteraction);
  },
  methods: {
    millisToMinutesAndSeconds(millis) {
      var minutes = Math.floor(millis / 60000);
      var seconds = ((millis % 60000) / 1000).toFixed(0);

      return [minutes, (seconds < 10 ? "0" : "") + seconds];
    },

    handleInteraction() {
      if (!this.showCountdownModal) {
        this.noInteractionTimer = 0;
      }
      //this.showCountdownBar = false;
    },
    resetTimer() {
      this.noInteractionTimer = 0;
      this.showCountdownModal = false;
    },

    logout() {
      clearAuthStorage();
      forgetIntendedPath();

      // Not reload(): signed out, the guard would remember the current page
      // again for the next login.
      window.location.href = "/login";
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard-layout {
  &__staging {
    @apply tw-border-t-4 tw-border-orange-500;
  }
}
</style>
